import React, { useState } from 'react';
import './AIStudio.scss';

const tools = [
  {
    id: 1,
    name: 'Text Generator',
    description: 'Generate blog posts, emails, and marketing copy in seconds.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
      </svg>
    ),
    color: '#6366F1',
    placeholder: 'Describe what you want to write about...',
  },
  {
    id: 2,
    name: 'Image Creator',
    description: 'Create stunning visuals and graphics for your content.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <path d="m21 15-5-5L5 21"/>
      </svg>
    ),
    color: '#EC4899',
    placeholder: 'Describe the image you want to create...',
  },
  {
    id: 3,
    name: 'Social Post',
    description: 'Generate engaging posts for all social platforms.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
      </svg>
    ),
    color: '#00D4AA',
    placeholder: 'What should your social post be about?',
  },
  {
    id: 4,
    name: 'Code Assistant',
    description: 'Get help with code, debug issues, and learn new technologies.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="16 18 22 12 16 6"/>
        <polyline points="8 6 2 12 8 18"/>
      </svg>
    ),
    color: '#F59E0B',
    placeholder: 'Paste your code or describe what you need...',
  },
];

const recentGenerations = [
  { type: 'text', title: 'Q4 Marketing Report', date: '2 hours ago', icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  )},
  { type: 'image', title: 'Product Launch Banner', date: '5 hours ago', icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <path d="m21 15-5-5L5 21"/>
    </svg>
  )},
  { type: 'social', title: 'Twitter Thread - AI Features', date: '1 day ago', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
    </svg>
  )},
];

export const AIStudio: React.FC = () => {
  const [activeTool, setActiveTool] = useState(tools[0]);
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState('');

  const handleGenerate = () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setTimeout(() => {
      setGenerating(false);
      setResult(`Generated content for: "${prompt}"\n\nThis is a sample output from the ${activeTool.name}. In a real implementation, this would connect to AI APIs like OpenAI, Anthropic, or Midjourney to generate actual content based on your prompt.`);
    }, 2000);
  };

  return (
    <div className="ai-studio-page">
      <div className="page-header">
        <h1 className="page-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="title-icon">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
          AI Studio
        </h1>
        <p className="page-subtitle">
          Supercharge your workflow with AI-powered creative tools.
        </p>
      </div>

      <div className="ai-studio-grid">
        {/* Tool Selector */}
        <div className="glass-card tools-card">
          <h3>AI Tools</h3>
          <div className="tools-list">
            {tools.map((tool) => (
              <button
                key={tool.id}
                className={`tool-btn ${activeTool.id === tool.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveTool(tool);
                  setPrompt('');
                  setResult('');
                }}
              >
                <div 
                  className="tool-icon"
                  style={{ background: `${tool.color}20`, color: tool.color }}
                >
                  {tool.icon}
                </div>
                <div className="tool-info">
                  <span className="tool-name">{tool.name}</span>
                  <span className="tool-desc">{tool.description}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Generation Interface */}
        <div className="glass-card generation-card">
          <div className="generation-header">
            <div 
              className="active-tool-icon"
              style={{ background: `${activeTool.color}20`, color: activeTool.color }}
            >
              {activeTool.icon}
            </div>
            <div className="active-tool-info">
              <h3>{activeTool.name}</h3>
              <p>{activeTool.description}</p>
            </div>
          </div>

          <div className="prompt-section">
            <div className="input-wrapper textarea glow">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={activeTool.placeholder}
                rows={4}
              />
            </div>
            <button 
              className="glass-button primary generate-btn"
              onClick={handleGenerate}
              disabled={generating || !prompt.trim()}
            >
              {generating ? (
                <>
                  <div className="spinner" />
                  Generating...
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                  </svg>
                  Generate
                </>
              )}
            </button>
          </div>

          {result && (
            <div className="result-section">
              <div className="result-header">
                <h4>Result</h4>
                <div className="result-actions">
                  <button className="icon-btn" title="Copy">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                  </button>
                  <button className="icon-btn" title="Download">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </button>
                  <button className="icon-btn" title="Share">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="18" cy="5" r="3"/>
                      <circle cx="6" cy="12" r="3"/>
                      <circle cx="18" cy="19" r="3"/>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="result-content">
                <pre>{result}</pre>
              </div>
            </div>
          )}
        </div>

        {/* Recent Generations */}
        <div className="glass-card recent-card">
          <h3>Recent</h3>
          <div className="recent-list">
            {recentGenerations.map((item, index) => (
              <div key={index} className="recent-item">
                <div className="recent-icon">
                  {item.icon}
                </div>
                <div className="recent-info">
                  <span className="recent-title">{item.title}</span>
                  <span className="recent-date">{item.date}</span>
                </div>
                <button className="recent-action">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="1"/>
                    <circle cx="19" cy="12" r="1"/>
                    <circle cx="5" cy="12" r="1"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
